import { supabase } from '../lib/supabase';

export interface ProductPhoto {
  id: string;
  stock_item_id: string;
  photo_url: string;
  created_at: string;
  updated_at: string;
}

export class ProductPhotoService {
  /**
   * Get photo for a single stock item
   */
  static async getPhotoByStockItemId(stockItemId: string): Promise<ProductPhoto | null> {
    const { data, error } = await supabase
      .from('product_photos')
      .select('*')
      .eq('stock_item_id', stockItemId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - item has no photo
        return null;
      }
      throw error;
    }

    return data;
  }

  /**
   * Get photos for multiple stock items
   */
  static async getPhotosByStockItemIds(stockItemIds: string[]): Promise<Record<string, ProductPhoto>> {
    if (stockItemIds.length === 0) return {};

    const { data, error } = await supabase
      .from('product_photos')
      .select('*')
      .in('stock_item_id', stockItemIds);

    if (error) throw error;

    // Convert array to object keyed by stock_item_id
    const photoMap: Record<string, ProductPhoto> = {};
    data?.forEach(photo => {
      photoMap[photo.stock_item_id] = photo;
    });

    return photoMap;
  }

  /**
   * Upload/update photo for a stock item
   */
  static async upsertPhoto(stockItemId: string, photoUrl: string): Promise<ProductPhoto> {
    // First check if a photo already exists
    const existingPhoto = await this.getPhotoByStockItemId(stockItemId);
    
    if (existingPhoto) {
      // Update existing photo
      const { data, error } = await supabase
        .from('product_photos')
        .update({ photo_url: photoUrl })
        .eq('stock_item_id', stockItemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Insert new photo
      const { data, error } = await supabase
        .from('product_photos')
        .insert({
          stock_item_id: stockItemId,
          photo_url: photoUrl,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  }

  /**
   * Delete photo for a stock item
   */
  static async deletePhoto(stockItemId: string): Promise<void> {
    const { error } = await supabase
      .from('product_photos')
      .delete()
      .eq('stock_item_id', stockItemId);

    if (error) throw error;
  }

  /**
   * Upload photo to Supabase Storage and save URL to database
   */
  static async uploadAndSavePhoto(stockItemId: string, file: File): Promise<string> {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${stockItemId}-${Date.now()}.${fileExt}`;
      const filePath = `product-photos/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-photos')
        .getPublicUrl(filePath);

      // Save URL to database
      await this.upsertPhoto(stockItemId, publicUrl);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  /**
   * Delete photo from storage and database
   */
  static async deletePhotoAndFile(stockItemId: string): Promise<void> {
    try {
      // Get current photo to find file path
      const currentPhoto = await this.getPhotoByStockItemId(stockItemId);
      
      if (currentPhoto) {
        // Extract file path from URL
        const url = new URL(currentPhoto.photo_url);
        const filePath = url.pathname.split('/').slice(-2).join('/'); // Get last two segments

        // Delete from storage
        const { error: storageError } = await supabase.storage
          .from('product-photos')
          .remove([filePath]);

        if (storageError) {
          console.warn('Error deleting file from storage:', storageError);
          // Continue with database deletion even if storage deletion fails
        }
      }

      // Delete from database
      await this.deletePhoto(stockItemId);
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  }
}
